import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, Subject, take, takeUntil } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { DepartmentSelectors } from '@core/department-data';
import type { Department } from '@core/departments-types';
import type { Requisition, RequisitionFlow } from '@core/requisitions-types';
import type { DialogRef } from '@core/user-interface-legacy';
import { DuplicateViewContext, RequisitionsSelectors } from '@recruitee/requisitions-data';
import {
  RequisitionsConfigSelectors,
  RequisitionsFlowsSelectors,
} from '@recruitee/requisitions-settings-data';

import { RequisitionsAnalytics } from '../analytics/requisitions.analytics';
import type { CreateRequisitionArgs } from '../requisitions.types';
import { CreateMode } from '../requisitions.types';
import { CreateDispatcher } from './create-dispatcher';
import { DuplicateDispatcher } from './duplicate-dispatcher';
import { RequisitionsDialogsService } from './requisitions-dialogs.service';

@Injectable()
export class RequisitionsCreateService {
  private withDepartments: boolean;
  private approvalFlows: RequisitionFlow[] = [];
  private departments: Department[] = [];
  private requisitions: Requisition[] = [];
  private duplicateDispatcher = new DuplicateDispatcher();
  private createDispatcher = new CreateDispatcher();

  private dialogRef: DialogRef | null;
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private requisitionsDialogs: RequisitionsDialogsService,
    private requisitionsAnalytics: RequisitionsAnalytics,
    private router: Router,
  ) {
    combineLatest([
      this.store.select(RequisitionsConfigSelectors.isDepartmentsMode),
      this.store.select(DepartmentSelectors.entities()).pipe(filterBoolean),
      this.store.select(RequisitionsFlowsSelectors.entities()).pipe(filterBoolean),
      this.store.select(RequisitionsSelectors.allActive()).pipe(filterBoolean),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([isDepartmentsMode, departments, approvalFlows, requisitions]) => {
        this.withDepartments = isDepartmentsMode;
        this.departments = departments;
        this.approvalFlows = approvalFlows;
        this.requisitions = requisitions;
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public create() {
    const canDuplicate = this.requisitions.length > 0;
    canDuplicate ? this.pickCreateMode$() : this.blank$(false);
  }

  private pickCreateMode$() {
    this.requisitionsDialogs
      .openCreateFirstStep$()
      .afterClosed.pipe(take(1))
      .subscribe(result => {
        if (result?.mode === CreateMode.Blank) {
          this.blank$(true);
        } else if (result?.mode === CreateMode.Duplicate) {
          this.duplicate$();
        }
      });
  }

  private blank$(isStep: boolean) {
    const data = {
      withDepartments: this.withDepartments,
      approvalFlows: this.approvalFlows,
      departments: this.departments,
      saveHandler: this.performCreate.bind(this),
      isStep,
    };

    this.dialogRef = this.requisitionsDialogs.openCreateSecondStepBlank$(data);
    this.dialogRef.afterClosed.pipe(take(1)).subscribe(result => {
      if (result?.goBack) this.pickCreateMode$();
      if (this.dialogRef) this.dialogRef = null;
    });
  }

  private duplicate$() {
    const data = {
      requisitions: this.requisitions,
      departments: this.departments,
      saveHandler: this.performDuplicate.bind(this),
    };
    this.dialogRef = this.requisitionsDialogs.openCreateSecondStepDuplicate$(data);
    this.dialogRef.afterClosed.pipe(take(1)).subscribe(result => {
      if (result?.goBack) this.pickCreateMode$();
      if (this.dialogRef) this.dialogRef = null;
    });
  }

  private performCreate(data: CreateRequisitionArgs) {
    this.createDispatcher
      .execute$(data)
      .pipe(take(1))
      .subscribe(action => {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
        this.requisitionsAnalytics.trackCreateNew('blank');
        this.redirect(action.payload.data.id);
      });
  }

  private performDuplicate(duplicateId: number): void {
    this.duplicateDispatcher
      .execute$(duplicateId, DuplicateViewContext.Dialog)
      .pipe(take(1))
      .subscribe(({ id }) => {
        if (this.dialogRef) {
          this.dialogRef.close();
          this.dialogRef = null;
        }
        this.requisitionsAnalytics.trackCreateNew('duplicate');
        this.redirect(id);
      });
  }

  private redirect(id: number) {
    this.router.navigateByUrl(`/requisitions/${id}`);
  }
}
